import { FunctionComponent, useState } from "react";
import BasicDrawer from "@components/drawers/BasicDrawer";
import { formValidateTriggers } from "@utils/Constant";
import { Form } from "antd";
import SubmitButton from "@components/buttons/SubmitButton";
import { toastSuccess } from "@utils/toast-helper";
import { ContactDto } from "@type/estimateInput/estimateInput.dto";
import MailFormField, { EMAIL_REGEX } from "@components/inputs/MailFormField";
import HTMLReactParser from "html-react-parser";
import { useTranslation } from "react-i18next";
import {useGoogleReCaptcha} from "@utils/react-google-recaptcha-v3/use-google-recaptcha";

interface ShareDrawerProps {
  source: string;
  visible: boolean;
  simulationId?: string;
  saveSimulation: (
    contactDto: ContactDto,
    successCallback: (simulationId: string) => void,
    errorCallback: () => void
  ) => void;
  onUpdateSimulationId: (id: string) => void;
  onClose: () => void;
  className?: string;
}

const ShareDrawer: FunctionComponent<ShareDrawerProps> = ({
  visible,
  saveSimulation,
  onClose,
  onUpdateSimulationId,
  className = "",
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isFormComplete, setIsFormComplete] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const buildContactDto = (token: string): ContactDto | undefined => {
    if (token) {
      const contactDto: ContactDto = form.getFieldsValue();
      contactDto.captcha = token;
      return contactDto;
    }
    return undefined;
  };

  const handleShareSimulation = async () => {
    if (executeRecaptcha) {
      const token = await executeRecaptcha();

      const contactDto = buildContactDto(token);
      if (contactDto) {
        setIsUploading(true);

        const successCallback = (simulationId: string) => {
          onUpdateSimulationId(simulationId);
          if (contactDto.email) {
            toastSuccess("Votre simulation a été partagée");
          }
          form.resetFields();
          setIsUploading(false);
        };

        const errorCallback = () => {
          setIsUploading(false);
        };
        saveSimulation(contactDto, successCallback, errorCallback);
      }
    }
  };

  const validateForm = () => {
    if (
      !form.getFieldsError().some((item) => item.errors.length > 0) &&
      form.getFieldValue("email") &&
      form.getFieldValue("email").match(EMAIL_REGEX)
    ) {
      setIsFormComplete(true);
    } else {
      setIsFormComplete(false);
    }
  };

  return (
    <BasicDrawer
      title="Partager votre simulation"
      visible={visible}
      onClose={onClose}
      className={className}
    >
      <>
        <Form
          {...formValidateTriggers}
          form={form}
          validateTrigger={"onChange"}
          layout="vertical"
          onFinish={handleShareSimulation}
          onValuesChange={validateForm}
        >
          <MailFormField
            required
            showLabel
            module="share"
            field="email"
            label={
              <span>
                {HTMLReactParser(
                  "Adresse email de votre choix <span className='font-weight-normal'>(exemple@gmail.com)</span>"
                )}
              </span>
            }
            placeholder="Adresse email de votre choix (exemple@gmail.com)"
            emptyErrorMessage="Votre adresse email pour enregistrer votre simulation"
            className="required-not-displayed"
          />
          <SubmitButton
            module="share"
            disabled={!isFormComplete || !executeRecaptcha || isUploading}
            text="Partager"
            variant="secondary"
            className="w-100"
          />
          <p className="text-sm mt-3">
            {HTMLReactParser(t("captcha.description"))}
          </p>
        </Form>
      </>
    </BasicDrawer>
  );
};

export default ShareDrawer;
